import React, { useState, useEffect, createRef, useCallback, useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';

import { makeStyles, useTheme, styled } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Popper from '@mui/material/Popper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup, { toggleButtonGroupClasses } from '@mui/material/ToggleButtonGroup';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';

import GetAppIcon from '@mui/icons-material/GetApp';
import PublishIcon from '@mui/icons-material/Publish';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';
import DefaultIcon from '@mui/icons-material/SettingsBackupRestore';

import { arrayMoveImmutable as arrayMove } from 'array-move';
import { toast } from 'react-toastify';

import PopupMenu from '../../../components/popupmenu';
import StyledPaper from '../../../components/styled/paper';
import ConfirmationDialog from '../../../components/confirmationdlg';
import MetadataService, { DefaultMapping, AllMapping } from '../../../data/metadata-service';
import DataContext from '../../../data/data-context';
import { blue } from '../../../data/colors';
import { SaveFile } from '../../../libraries/helpers';
import ListWrapper from '../components/column-selector';

const useStyles = makeStyles(theme => ({
    root: {
        zIndex: theme.zIndex.modal + 3,
        width: 793,
        minHeight: 500,
        marginTop: 35,
    },
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500]
    },
    chip: {
        margin: theme.spacing(0.5),
        '&.hidden .MuiChip-label': {
            width: 150,
        },
        '&:not(.hidden), .MuiChip-label': {
            width: 134,
        },
    },
    grid: {
        display: 'grid',
        gridGap: 0,
        gridTemplateColumns: 'auto auto auto auto auto',
        width: 'auto',
        maxHeight: 130,
        whiteSpace: 'nowrap',
        border: 0,
        backgroundColor: 'transparent',
        overflow: 'auto',
        position: 'relative',
        zIndex: 0,
        borderRadius: 3,
        outline: 'none',
    },

    gridItem: {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        outline: 'none',

        '&:focus': {
            textIndent: -2,
        },

        '& .wrapper': {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            transform: 'scale(1)',
            span: {
                display: 'none',
            }
        }
    },
    disabled: {
        cursor: 'not-allowed',
    },
    uploadBtn: {
        width: 25,
        minWidth: 0,
    },
    dataSources: {
        display: 'flex',
        flexWrap: 'wrap',
        maxHeight: 130,
        overflow: 'auto',
        width: 'auto',
    },
}));

// multi row toggle button group is just easier without borders
const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
    [`& .${toggleButtonGroupClasses.grouped}`]: {
        margin: theme.spacing(0.25),
        border: 0,
        borderRadius: 0,
        [`&.${toggleButtonGroupClasses.disabled}`]: {
            border: 0,
        },
    },

    [`& .${toggleButtonGroupClasses.middleButton},& .${toggleButtonGroupClasses.lastButton}`]: {
        borderLeft: '1px solid transparent',
    },
}));

const ColumnSelector = (props) => {
    const container = useRef(null);
    const classes = useStyles();
    const theme = useTheme();
    const [search, setSearch] = useState('');
    const [sources, setSources] = useState([]);
    const [columns, setColumns] = useState([]);
    const [sort, setSort] = useState({});
    const [availableColumns, setAvailableColumns] = useState([]);
    const [savedViews, setSavedViews] = useState([]);
    const [savedView, setSavedView] = useState();
    const [deleteSavedView, setDeleteSavedView] = useState();
    const [getSavedViewName, setGetSavedViewName] = useState();

    const handleSearchChanged = event => {
        setSearch(event.target.value || '');
    };

    const isAvailable = useCallback(c => c.sticky || sources.some(s =>
        MetadataService.sources[s] && MetadataService.sources[s][c.variable]), [sources]);

    const inSearch = useCallback(c =>
        search.length < 0 ||
        (c.variable + c.title).toLowerCase().includes(search.toLowerCase()), [search]);

    const isVisible = useCallback(c =>
        !c.hide && !c.columnSelectorHidden && inSearch(c), [inSearch]);

    const isNotvisible = useCallback(c =>
        isAvailable(c) &&
        !!c.hide &&
        !c.columnSelectorHidden &&
        inSearch(c), [inSearch, isAvailable]);

    const isSorted = c => c.field === sort.field ? sort.sort === 'desc' ? -1 : 1 : 0;

    const handleOnDefaults = () => {
        const currentmap = columns.reduce((a, c) => ({ ...a, [c.variable]: c }), {});
        const cols = props.fields
            .filter(f => !f.tableHidden)
            .map(f => {
                const { order, hide, ...rest } = currentmap[f.variable];
                return {
                    ...f,
                    ...rest,
                    hide: f.addon ? rest.columnSelectorHidden : f.hide
                };
            })
            .sort((a, b) => {
                if (a.hide && !b.hide) return 1;
                if (b.hide && !a.hide) return -1;
                return (a.order || 0) - (b.order || 0);
            });
        if (MetadataService.sources.conn) props.setSources(['conn']);
        props.setColumns(cols);
        props.setSort({ field: '_source.timestamp', sort: 'desc' });
        props.setSavedView();
    };

    const handleSourcesChange = (e, sources) => {
        props.setSources(sources);
    };

    const handleSortColumnChanged = c => {
        props.setSort({
            field: c.field,
            sort: sort.field === c.field && sort.sort === 'desc' ? 'asc' : 'desc'
        });
    };

    const handleSortEnded = (oldIndex, newIndex) => {
        // the proviced indices are from filtered lists. Need to find the real index.
        const filtered = columns.filter(c => !c.hide && !c.columnSelectorHidden);
        const src = filtered[oldIndex];
        const dest = filtered[newIndex];
        const oi = columns.findIndex(c => c.variable === src.variable);
        const ni = columns.findIndex(c => c.variable === dest.variable);
        const updated = arrayMove(columns, oi, ni);
        props.setColumns(updated);
    };

    const handleRemoveColumn = (c) => {
        const i = columns.findIndex(col => col.variable === c.variable);

        // bubble to the top of the hidden columns
        const lastvisible = columns.map(c => !c.hide && !c.columnSelectorHidden).lastIndexOf(true);
        columns[i].hide = true;
        const updated = arrayMove(columns, i, lastvisible - 1);
        props.setColumns(updated);

        // move sorting if needed
        if (sort.field === c.field) {
            let j = 0;
            let next = columns[j];
            while (j < columns.length - 1 && (!next.sortable || next.hide)) {
                j++;
                next = columns[j];
            }
            props.setSort({ field: next.field, sort: sort.sort });
        }
    };

    const handleAddColumn = (c) => {
        const i = columns.findIndex(col => col.variable === c.variable);

        // bubble to the buttom of the visible columns
        const lastvisible = columns.map(c => !c.hide && !c.columnSelectorHidden).lastIndexOf(true);
        columns[i].hide = false;
        const updated = arrayMove(columns, i, lastvisible + 1);
        props.setColumns(updated);
    };

    const handleOnSelectSavedView = f => {
        if (f && savedView && (f.uuid === savedView.uuid)) {
            // deselect but don't refresh the page
            props.setSavedView();
            return;
        }
        if (!f.data) return;
        applyView(f.data);
        props.setSavedView(f);
    };

    const applyView = data => {
        const savedColumns = data.columns || {};
        const currentmap = columns.reduce((a, c) => ({ ...a, [c.variable]: c }), {});
        const cols = props.fields
            .filter(c => !c.tableHidden)
            .map(c => {
                const { order, hide, ...rest } = currentmap[c.variable];
                return {
                    ...c,
                    ...rest,
                    hide: c.addon ? rest.columnSelectorHidden : savedColumns[c.variable] === undefined,
                    order: savedColumns[c.variable]?.order || c.order,
                };
            })
            .sort((a, b) => {
                if (a.hide && !b.hide) return 1;
                if (b.hide && !a.hide) return -1;
                return (a.order || 0) - (b.order || 0);
            });

        props.setSort(data.sort || sort);
        props.setColumns(cols);
        props.setSources(current => data.sources || current);
    };

    const handleOnDeleteSavedView = () => {
        handleDeleteSavedView(deleteSavedView);
        setDeleteSavedView(undefined);
    };

    const handleAddNewView = () => {
        setGetSavedViewName(true);
    };

    const handleOnSaveView = () => {
        if (savedView) {
            const visibleColumns = (columns || []).filter(c => !c.hide);

            const f = {
                ...savedView,
                data: {
                    columns: visibleColumns.reduce((a, c, i) => {
                        return { ...a, [c.variable]: { order: i } };
                    }, {}),
                    sources,
                    sort,
                },
            };
            handleUpsertView(f);
        }
    };

    const handleOnNewView = name => {
        const visibleColumns = (columns || []).filter(c => !c.hide);

        const f = {
            name,
            data: {
                columns: visibleColumns.reduce((a, c, i) => {
                    return { ...a, [c.variable]: { order: i } };
                }, {}),
                sources,
                sort,
            },
        };

        props.setSavedView(f);
        handleUpsertView(f);
        setGetSavedViewName(false);
    };

    const handleUpsertView = f => {
        MetadataService.upsertSavedView(f)
            .then(res => props.setSavedViews(res)).catch(err => console.error(err));
    };

    const handleDeleteSavedView = f => {
        MetadataService.deleteSavedView(f.uuid)
            .then(res => props.setSavedViews(res)).catch(err => console.error(err));
    };

    const handleOnImport = e => {
        const fileHandler = (file) => {
            const reader = new FileReader();
            return new Promise((resolve, reject) => {
                reader.onload = event => resolve(event.target.result);
                reader.onerror = error => reject(error);
                reader.readAsText(file);
            });
        };
        fileHandler(e.target.files[0]).then(content => {
            try {
                const data = JSON.parse(content);
                processViewImportFile(data);
            } catch (err) {
                toast.error('Import file is not a valid Metadata column view file');
                console.error(err);
            }
        }).catch(err => {
            toast.error('Cannot import Metadata view file');
            console.error(err);
        });
    };

    const processViewImportFile = data => {
        // backward compatibility
        if (data.data) {
            data = data.data;
        }
        data = maybeConvert(data);
        applyView(data);
        props.setSavedView();
    };

    const handleOnExport = () => {
        const visibleColumns = (columns || []).filter(c => !c.hide);
        const data = {
            columns: visibleColumns.reduce((a, c, i) => {
                return { ...a, [c.variable]: { order: i } };
            }, {}),
            sources,
            sort,
        };
        SaveFile(
            JSON.stringify(data),
            `${savedView ? savedView.name : 'metadata_view'}.json`,
            'text/json;charset=utf-8;'
        );
    };

    const maybeConvert = data => {
        try {
            data = JSON.parse(data);
            data = {
                columns: (data.columns || []).reduce((a, c, i) => ({
                    ...a,
                    [c.name]: { order: i },
                }), {}),
                sort: {
                    field: MetadataService.columnsMap[data.sortCol].field,
                    sort: data.sortOrder ? 'asc' : 'desc',
                },
                sources: Object.keys(data.selDataTypes || {})
                    .filter(k => data.selDataTypes[k]),
            };
        } catch {
            // no conversion needed
        }
        return data;
    };

    useEffect(() => {
        setSavedView(props.savedView);
    }, [props.savedView]);

    // load up saved column sets
    useEffect(() => {
        const views = (props.savedViews || []).map(v => {
            let data = v.data;
            data = maybeConvert(v.data);
            return {
                ...v,
                data,
            };
        });
        setSavedViews(views);
    }, [props.savedViews]);

    useEffect(() => {
        setSources(props.sources);
    }, [props.sources]);

    useEffect(() => {
        // copy
        setColumns(props.columns);
    }, [props.columns]);

    useEffect(() => {
        setSort({ field: props.sortCol, sort: props.sortOrder });
    }, [props.sortCol, props.sortOrder]);

    useEffect(() => {
        setAvailableColumns(columns
            .filter(isNotvisible)
            .sort((a, b) => (a.title || a.variable).localeCompare(b.title || b.variable))
        );
    }, [columns, isNotvisible]);

    return (
        <div ref={container}>
            <DialogContent dividers style={{ marginTop: 0, paddingTop: 10, paddingBottom: 0 }}>
                <Typography variant="subtitle1">
                    Saved Views
                    {/* saved views */}
                    <PopupMenu
                        tooltip="save view"
                        items={savedViews || []}
                        itemkey="name"
                        onSelect={handleOnSelectSavedView}
                        icon={<SaveIcon/>}
                        placement={'bottom-start'}
                        altaction={setDeleteSavedView}
                        actionicon={<DeleteIcon color="secondary" fontSize="small"/>}
                        selection={savedView ? savedView.name : undefined}
                        moreActions={[
                            {
                                icon: <AddIcon fontSize="small" color="primary"/>,
                                title: 'Add New Saved view',
                                key: 'new',
                                callback: handleUpsertView ? handleAddNewView : undefined,
                                divider: true,
                            },
                            {
                                icon: <SaveIcon fontSize="small" color="primary"/>,
                                title: savedView ? `Update ${savedView.name}` : undefined,
                                key: 'update',
                                callback: savedView ? handleOnSaveView : undefined,
                                divider: true,
                            },
                            {
                                icon: <DefaultIcon fontSize="small" color="primary"/>,
                                title: 'Default',
                                key: 'default',
                                callback: handleOnDefaults,
                                divider: true,
                            },
                            {
                                icon: <GetAppIcon fontSize="small" color="primary"/>,
                                title: 'Export View',
                                key: 'export',
                                callback: handleOnExport,
                            },
                            {
                                icon: <label htmlFor="col-import-file">
                                    <Button component="div" className={classes.uploadBtn}>
                                        <PublishIcon fontSize="small" color="primary"/>
                                    </Button>
                                    Import View
                                </label>,
                                title: '',
                                key: 'import',
                                callback: () => {},
                                clickable: false,
                                divider: true,
                            },
                        ]}/>
                    <input
                        id="col-import-file"
                        style={{ display: 'none' }}
                        aria-label="handle import file"
                        type="file"
                        onClick={(e) => { e.target.value = null }}
                        onChange={handleOnImport}/>
                </Typography>
            </DialogContent>

            <DialogContent dividers>
                <Typography variant="subtitle1">
                    Data Sources
                </Typography>
                <StyledToggleButtonGroup
                    className={classes.dataSources}
                    size="small"
                    value={sources}
                    onChange={handleSourcesChange}>
                    {Object.entries(MetadataService.sources)
                        .sort()
                        .map(([k, v], i) => (
                            <ToggleButton key={`${k}_${i}`} aria-label={k} value={k}>
                                {k}
                            </ToggleButton>
                        ))}
                </StyledToggleButtonGroup>
            </DialogContent>

            <DialogContent dividers style={{ marginTop: 0, paddingTop: 10 }}>
                <Grid container spacing={1} direction="row" justifyContent="space-between">
                    <Grid item p={1}>
                        <Typography variant="subtitle1">
                            Selected Columns ({columns.filter(c => !c.hide).length})
                        </Typography>
                    </Grid>
                    <Grid item p={1} style={{ paddingBottom: 20 }}>
                        <TextField
                            value={search}
                            onChange={handleSearchChanged}
                            placeholder="Search..."
                            variant="outlined"
                            margin="dense"
                            style={{ margin: 0 }}
                            InputProps={{
                                endAdornment: <InputAdornment position="end">
                                    <React.Fragment>
                                        <IconButton
                                            disabled={!search}
                                            aria-label="clear"
                                            title="clear"
                                            onClick={() => setSearch('')}>
                                            <ClearIcon fontSize="small" />
                                        </IconButton>
                                        <SearchIcon fontSize="inherit" />
                                    </React.Fragment>
                                </InputAdornment>
                            }}/>
                    </Grid>
                </Grid>
                <div>
                    <ListWrapper
                        helperContainer={container.current}
                        classes={classes}
                        axis={'xy'}
                        items={columns.filter(isVisible)}
                        className={classes.grid}
                        itemClass={classes.gridItem}
                        onSort={handleSortColumnChanged}
                        isAvailable={isAvailable}
                        isSorted={isSorted}
                        onSortEnd={handleSortEnded}
                        onAction={c => c.sortable && handleSortColumnChanged(c)}
                        onDelete={c => handleRemoveColumn(c)}
                        isDisabled={false}
                        getColor={c => 'secondary'}/>
                </div>
            </DialogContent>
            <DialogContent dividers>
                <Grid item p={1}>
                    <Typography variant="subtitle1">
                        Available Columns ({availableColumns.length})
                    </Typography>
                </Grid>
                <div style={{ paddingTop: 15 }}>
                    <ListWrapper
                        helperContainer={container.current}
                        classes={classes}
                        axis={'xy'}
                        items={availableColumns}
                        className={classes.grid}
                        itemClass={classes.gridItem}
                        onAction={handleAddColumn}
                        isDisabled={true}
                        getColor={c => 'primary'}/>
                </div>
            </DialogContent>

            {/* delete confirmation dlg */}
            {deleteSavedView &&
                <ConfirmationDialog
                    style={{ zIndex: theme.zIndex.modal + 4 }}
                    onClose={() => setDeleteSavedView(undefined)}
                    onOK={handleOnDeleteSavedView}
                    open={true}
                    message={`Delete saved view '${deleteSavedView.name}'?`}/>}

            {/* get new saved filter name */}
            {getSavedViewName &&
                <ColumnViewNameDialog
                    onClose={() => setGetSavedViewName(undefined)}
                    onOK={handleOnNewView}
                    open={true}
                    existing={(savedViews || []).map(f => f.name)}/>}
        </div>
    );
};
// custom toolbar props
ColumnSelector.propTypes = {
    columns: PropTypes.array,
    sources: PropTypes.array,
    fields: PropTypes.array,
    sortCol: PropTypes.string,
    sortOrder: PropTypes.string,
    savedViews: PropTypes.array,
    savedView: PropTypes.object,
    setSources: PropTypes.func,
    setSort: PropTypes.func,
    setColumns: PropTypes.func,
    setSavedViews: PropTypes.func,
    setSavedView: PropTypes.func,
};

const ColumnMapper = (props) => {
    const classes = useStyles();
    const theme = useTheme();
    const [mappings, setMappings] = useState({});
    const [columns, setColumns] = useState([]);
    const [current, setCurrent] = useState(0);
    const [availableColumns, setAvailableColumns] = useState([]);
    const { metadataMappings } = useContext(DataContext);
    const container = useRef(null);

    const handleSortEnded = (k, o, n) => {
        if (!mappings[k]) return;

        props.setMappings({
            ...mappings,
            [k]: arrayMove(mappings[k], o, n)
        });
    };

    const isAllowed = c => {
        const k = columns[current];
        const typ = c.type === MetadataService.columnsMap[k].type;
        const dup = mappings[k].find(cc => cc === c.variable) !== undefined;
        return typ && !dup;
    };

    const handleAddColumn = (c) => {
        if (columns.length <= current) return;

        const k = columns[current];
        if (!isAllowed(c)) return;

        props.setMappings({
            ...mappings,
            [k]: [...mappings[k], c.variable],
        });
    };

    const handleRemoveColumn = (k, c) => {
        if (!mappings[k]) return;

        const values = mappings[k].filter(cc => cc !== c.variable);
        // at least one value is required
        if (!values.length) values.push(k);

        props.setMappings({
            ...mappings,
            [k]: values,
        });
    };

    useEffect(() => {
        const all = Object.entries(AllMapping)
            .reduce((a, [k, v]) => [...a, ...v], [])
            .map(k => MetadataService.columnsMap[k])
            .sort((a, b) => (a.title || a.variable).localeCompare(b.title || b.variable))
            // dedup with O(n) after sorting
            .filter((c, i, a) => !i || c !== a[i - 1]);
        // all columns in alphabetical order
        setAvailableColumns(all);
    }, [metadataMappings]);

    useEffect(() => {
        setColumns(Object.keys(props.mappings || {}));
        setMappings(props.mappings || {});
    }, [props.mappings]);

    return (
        <div ref={container}>
            <DialogContent dividers style={{ marginTop: 0, paddingTop: 10 }}>
                <Grid container spacing={1} direction="row">
                    <Grid item p={1}>
                        <Typography variant="subtitle1">
                                Column Mapping
                        </Typography>
                    </Grid>
                </Grid>
                {/* column categories */}
                <Grid container
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start">
                    {
                        columns.map((k, i) =>
                            <Grid key={k} container
                                style={{ width: 'auto' }}
                                direction="column"
                                justifyContent="flex-start"
                                alignItems="flex-start">
                                {/* column mapping category */}
                                <Grid item key={k} style={{ marginBottom: 10 }}>
                                    <Chip
                                        key={k}
                                        clickable={false}
                                        size='small'
                                        style={{
                                            backgroundColor: current === i
                                                ? theme.palette.primary.main
                                                : blue[100],
                                            color: current === i
                                                ? theme.palette.getContrastText(theme.palette.primary.main)
                                                : theme.palette.getContrastText(blue[100]),
                                            fontWeight: 'bold',
                                            marginBottom: 7,
                                        }}
                                        label={MetadataService.columnsMap[k].title || MetadataService.columnsMap[k].variable}
                                        className={clsx(classes.chip)}
                                        onClick={() => setCurrent(i)}/>
                                </Grid>
                                {/* column mapping values÷ */}
                                <ListWrapper
                                    helperContainer={container.current}
                                    classes={classes}
                                    axis={'y'}
                                    lockAxis='y'
                                    items={(mappings[k] || []).map(c => MetadataService.columnsMap[c])}
                                    itemClass={classes.gridItem}
                                    getColor={c => 'primary'}
                                    chipStyle={{
                                        backgroundColor: current === i
                                            ? blue[400]
                                            : blue[100],
                                        color: current === i
                                            ? theme.palette.getContrastText(blue[400])
                                            : theme.palette.getContrastText(blue[100]),
                                    }}
                                    onDelete={c => handleRemoveColumn(k, c)}
                                    isDisabled={false}
                                    onSortEnd={(o, n) => handleSortEnded(k, o, n)}/>
                            </Grid>
                        )
                    }
                </Grid>
            </DialogContent>
            <DialogContent dividers>
                <Grid item p={1}>
                    <Typography variant="subtitle1">
                            Select Columns to Map
                    </Typography>
                </Grid>
                {/* available columns */}
                <div style={{ paddingTop: 15 }}>
                    <ListWrapper
                        helperContainer={container.current}
                        classes={classes}
                        items={availableColumns.filter(isAllowed)}
                        className={classes.grid}
                        itemClass={classes.gridItem}
                        onAction={c => handleAddColumn(c)}
                        getColor={c => 'primary'}
                        chipStyle={{
                            backgroundColor: blue[400],
                            color: theme.palette.getContrastText(blue[400]),
                        }}
                        isDisabled={true}/>
                </div>
            </DialogContent>
        </div>
    );
};
ColumnMapper.propTypes = {
    mappings: PropTypes.object,
    setMappings: PropTypes.func,
};

const ColumnMain = (props) => {
    const classes = useStyles();
    const dialogRef = createRef();
    const [sources, setSources] = useState([]);
    const [columns, setColumns] = useState([]);
    const [sort, setSort] = useState({});
    const [savedViews, setSavedViews] = useState([]);
    const [savedView, setSavedView] = useState({});
    const [mappings, setMappings] = useState({});
    const [tab, setTab] = useState('select');

    function handleKeyDown (event) {
        if (event.key === 'Escape') {
            event.preventDefault();
            props.onClickAway();
        }
    }

    const handleOnDefaults = () => {
        const currentmap = columns.reduce((a, c) => ({ ...a, [c.variable]: c }), {});
        const cols = props.fields
            .filter(f => !f.tableHidden)
            .map(f => {
                const { order, hide, ...rest } = currentmap[f.variable];
                return {
                    ...f,
                    ...rest,
                    hide: f.addon ? rest.columnSelectorHidden : f.hide
                };
            })
            .sort((a, b) => {
                if (a.hide && !b.hide) return 1;
                if (b.hide && !a.hide) return -1;
                return (a.order || 0) - (b.order || 0);
            });
        if (MetadataService.sources.conn) setSources(['conn']);
        setColumns(cols);
        setSort({ field: '_source.timestamp', sort: 'desc' });
        setSavedView();
    };

    const handleOnOK = () => {
        props.onOK({ columns, sources, sort, mappings });
        props.onClickAway();
    };

    const handleOnReset = () => {
        setMappings({ ...DefaultMapping });
    };

    useEffect(() => {
        // update current selection if it's still in the list (i.e removed by someone else)
        setSavedView(current => {
            if (current) {
                return (savedViews || []).find(f => f.name === current.name);
            } else return current;
        });
    }, [savedViews]);

    // load up saved column sets
    useEffect(() => {
        (async () => {
            MetadataService.getSavedViews()
                .then(d => setSavedViews(d))
                .catch(err => console.errror(err));
        })();
    }, [setSavedViews]);

    useEffect(() => {
        setSources(props.sources);
    }, [props.sources]);

    useEffect(() => {
        // copy
        setColumns(props.columns.map(c => ({ ...c })));
    }, [props.columns]);

    useEffect(() => {
        setSort(props.sort && props.sort.length > 0 ? props.sort[0] : {});
    }, [props.sort]);

    useEffect(() => {
        // copy
        setMappings({ ...props.mappings || {} });
    }, [props.mappings]);

    return (
        <Popper
            className={classes.root}
            open={props.show}
            anchorEl={props.anchorEl}
            placement={props.placement || 'bottom-start'}
            modifiers={[
                { name: 'flip', enabled: false, options: { altBoundary: true, rootBoundary: 'document' } },
                { name: 'preventOverflow', enabled: true, options: { tether: true, rootBoundary: 'document' } },
            ]}
            role={undefined} transition disablePortal>
            {({ TransitionProps, placement }) => (
                <StyledPaper ref={dialogRef}>
                    {/* dialog title */}
                    <div onKeyDown={handleKeyDown}>
                        <DialogTitle>
                            <Button aria-label="Column Mapping" onClick={() => setTab('select')} color="secondary">
                            Select Columns
                            </Button>
                            <Button aria-label="Column Mapping" onClick={() => setTab('map')} color="secondary">
                            Column Mapping
                            </Button>
                            <IconButton
                                aria-label="close"
                                className={classes.closeButton}
                                onClick={props.onClickAway}
                                size="large">
                                <CloseIcon fontSize="small"/>
                            </IconButton>
                        </DialogTitle>
                    </div>

                    {/* dialog content */}
                    <div>
                        {{
                            select: <ColumnSelector
                                columns={columns}
                                sort={sort}
                                sources={sources}
                                fields={props.fields}
                                savedViews={savedViews}
                                savedView={savedView}
                                setSources={setSources}
                                setSort={setSort}
                                setColumns={setColumns}
                                setSavedViews={setSavedViews}
                                setSavedView={setSavedView}/>,
                            map: <ColumnMapper
                                mappings={mappings}
                                setMappings={setMappings}/>,
                        }[tab]}
                    </div>

                    {/* dialog actions */}
                    <DialogActions>
                        {tab === 'select' &&
                            <div style={{ flex: '1 0 0' }}>
                                <Button aria-label="Defaults" onClick={handleOnDefaults} color="primary">
                                    Defaults
                                </Button>
                            </div>
                        }
                        {tab === 'map' &&
                            <div style={{ flex: '1 0 0' }}>
                                <Button aria-label="Reset" onClick={handleOnReset} color="primary">
                                    Reset
                                </Button>
                            </div>
                        }
                        <Button autoFocus aria-label="Cancel" onClick={props.onClickAway} color="primary">
                            Cancel
                        </Button>
                        <Button aria-label="OK" onClick={handleOnOK} color="primary">
                            OK
                        </Button>
                    </DialogActions>

                </StyledPaper>
            )}
        </Popper>
    );
};
ColumnMain.propTypes = {
    show: PropTypes.bool.isRequired,
    placement: PropTypes.string,
    anchorEl: PropTypes.object,
    onClickAway: PropTypes.func.isRequired,
    columns: PropTypes.array,
    sources: PropTypes.array,
    fields: PropTypes.array,
    mappings: PropTypes.object,
    sort: PropTypes.array,
    onOK: PropTypes.func
};

function ColumnViewNameDialog ({ existing = [], ...props }) {
    const theme = useTheme();

    const [name, setName] = React.useState('');
    const { onClose, onOK, open, ...rest } = props;

    const handleChange = (event) => {
        setName(event.target.value);
    };

    const duplicate = () => {
        return (existing || []).find(e => e === name) !== undefined;
    };

    // todo: add regex validation
    const badName = () => {
        return !name || name.length < 1;
    };

    const valid = () => {
        return !duplicate() && !badName();
    };

    const handleCloseBackdrop = (e, reason) => {
        console.log('reason', reason);
    };

    return (
        <Dialog
            style={{ zIndex: theme.zIndex.modal + 4 }}
            onClose={handleCloseBackdrop}
            disableEscapeKeyDown
            maxWidth="xs"
            aria-labelledby="New Saved View"
            open={open}
            {...rest}>
            <DialogTitle id="confirmation-dialog-title" >
                New Saved View
            </DialogTitle>
            <DialogContent dividers>
                <form noValidate autoComplete="off">
                    <TextField
                        style={{ minWidth: 250 }}
                        error={!valid()}
                        label="Name"
                        value={name}
                        onChange={handleChange}
                    />
                </form>
            </DialogContent>
            <DialogActions>
                <Button autoFocus onClick={props.onClose} color="primary" aria-label="cancel">
                    Cancel
                </Button>
                <Button onClick={() => props.onOK(name)} color="primary" disabled={!valid()} aria-label="save">
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
}

ColumnViewNameDialog.propTypes = {
    onClose: PropTypes.func.isRequired,
    onOK: PropTypes.func.isRequired,
    open: PropTypes.bool.isRequired,
    existing: PropTypes.array,
};

export {
    ColumnMain as default,
};
